import {
  ReactElement,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, FullLoader, Modal, ModalFooter } from '@sc/shared-ui';
import {
  useApolloClient,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@apollo/client/react';
import { getFragmentData, SubscriptionType } from '@sc/graphql';
import { ESSENTIALS_STEPS } from '@sc/stripe/pricing';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useStore } from '@tanstack/react-form';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import env from '../environment';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';
import { NewCardForm, NewCardFormHandle } from '../components/NewCardForm';
import { AccountDetailsGate } from '../components/AccountDetailsGate';
import { useAppForm } from '../forms/appForm';
import {
  BillingDetailsFields,
  EMPTY_BILLING_DETAILS,
  toBillingDetailsInput,
} from '../components/BillingDetailsFields';
import {
  ADD_NEW_PAYMENT_METHOD,
  PaymentMethodSelector,
} from '../components/PaymentMethodSelector';
import { TaxSummary } from '../components/TaxSummary';
import { formatGbp } from '../utility/money';
import {
  GQL_FRAGMENT_USER,
  GQL_MUTATION_CREATE_SETUP_INTENT,
  GQL_MUTATION_CREATE_SUBSCRIPTION_INTENT,
  GQL_MUTATION_SAVE_PAYMENT_METHOD,
  GQL_QUERY_LIST_PAYMENT_METHODS,
  GQL_QUERY_ME,
  GQL_QUERY_PREVIEW_PLAN_CHANGE,
  GQL_QUERY_READ_USAGE,
} from '../utility/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Index into ESSENTIALS_STEPS — the slider position being purchased. */
  priceStep: number;
  onSuccess?: (() => void) | undefined;
}

const stripePromise = env.stripePublishableKey
  ? loadStripe(env.stripePublishableKey)
  : null;

const CheckoutForm = ({
  priceStep,
  onClose,
  onSuccess,
  loading,
  setLoading,
}: {
  priceStep: number;
  onClose: () => void;
  onSuccess?: (() => void) | undefined;
  loading: boolean;
  setLoading: (next: boolean) => void;
}): ReactElement => {
  const stripe = useStripe();
  const { user } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const apolloClient = useApolloClient();
  const newCardRef = useRef<NewCardFormHandle>(null);

  const { data } = useSuspenseQuery(GQL_QUERY_LIST_PAYMENT_METHODS);
  const cards = data.listPaymentMethods;

  const [createSetupIntent] = useMutation(GQL_MUTATION_CREATE_SETUP_INTENT, {
    onError,
  });

  const [savePaymentMethod] = useMutation(GQL_MUTATION_SAVE_PAYMENT_METHOD, {
    onError,
  });

  const [createSubscriptionIntent] = useMutation(
    GQL_MUTATION_CREATE_SUBSCRIPTION_INTENT,
    { onError }
  );

  const [cardError, setCardError] = useState<string | null>(null);

  // The monthly-plan card is the natural default; fall back to the first card
  // on file. When there are no saved cards, skip the picker and start straight
  // on the billing-address step. Adding a new card is two stages: address,
  // then the card itself.
  const defaultCard = cards.find((card) => card.useForMonthlyPlan) ?? cards[0];

  // A complete stored company address (set at a previous checkout or in
  // company settings) removes the address stage entirely — the server falls
  // back to it for tax determination when no billing details are sent.
  const hasCompanyAddress = Boolean(
    user.company.billingCountry &&
      user.company.billingAddressLine1 &&
      user.company.billingCity &&
      user.company.billingPostalCode
  );

  const firstCardStep = hasCompanyAddress ? 'card' : 'address';

  const [step, setStep] = useState<'address' | 'card' | 'pick'>(
    cards.length > 0 ? 'pick' : firstCardStep
  );

  useEffect(() => {
    track('Checkout Stage Viewed', {
      stage: step,
      subscriptionType: SubscriptionType.essentials,
    });
  }, [step]);

  const selectedStep = ESSENTIALS_STEPS[priceStep];

  const plan = selectedStep
    ? {
        name: `Essentials — ${selectedStep.queries.toLocaleString('en-GB')} queries + ${selectedStep.pages.toLocaleString('en-GB')} pages / month`,
        price: selectedStep.priceGbp,
      }
    : null;

  // Mid-cycle upgrades credit the unused portion of the current allowance
  // against today's charge — mirror the server's preview so the summary
  // matches the actual PaymentIntent amount.
  const { data: previewData } = useQuery(GQL_QUERY_PREVIEW_PLAN_CHANGE, {
    variables: { priceStep },
    skip: !user.company.subscription,
    fetchPolicy: 'network-only',
  });

  const upgradeDiscount = previewData?.previewPlanChange.discount ?? 0;

  const chargeToday = plan
    ? Math.max(plan.price - upgradeDiscount, 0)
    : 0;

  const form = useAppForm({
    defaultValues: {
      selected:
        cards.length > 0
          ? (defaultCard?.uuid ?? '')
          : ADD_NEW_PAYMENT_METHOD,
      billing: EMPTY_BILLING_DETAILS,
    },
    onSubmit: async ({ value }) => {
      if (!stripe) return;

      // Stage transitions. Field validators only run for mounted fields, so
      // the card picker gates 'pick', the billing fields gate 'address', and
      // the card fields gate the final submit.
      if (value.selected === ADD_NEW_PAYMENT_METHOD && step === 'pick') {
        setStep(firstCardStep);
        return;
      }

      if (step === 'address') {
        setStep('card');
        return;
      }

      setLoading(true);

      setCardError(null);

      track('Checkout Submitted', {
        subscriptionType: SubscriptionType.essentials,
        isUpgrade: !!user.company.subscription,
      });

      let hasCompleted = false;

      // Wrapped in try/finally so `loading` is always released — the modal
      // blocks closing while loading, so a stuck flag would trap the user.
      try {
        let paymentMethodId: string | null = null;
        let paymentMethodUuid: string | null = null;

        if (value.selected === ADD_NEW_PAYMENT_METHOD) {
          // 1. Create a SetupIntent scoped to this customer, then confirm it
          // with the entered card (NewCardForm validates and attaches it).
          const { data: setupData } = await createSetupIntent();
          const setupClientSecret = setupData?.createSetupIntent.clientSecret;

          if (!setupClientSecret) return;

          const result = await newCardRef.current?.confirm(setupClientSecret);

          if (!result?.paymentMethodId) {
            if (result?.error) setCardError(result.error);
            return;
          }

          paymentMethodId = result.paymentMethodId;

          // 2. Persist the card locally. This runs the tax determination and
          // refuses the card (payment error) if the treatment is blocked.
          const { data: saved } = await savePaymentMethod({
            variables: {
              input: {
                paymentMethodId,
                paymentMethodUuid: null,
                useForMonthlyPlan: null,
                // With the address stage skipped, send no details — the tax
                // determination reuses the company's stored address.
                billingDetails: hasCompanyAddress
                  ? null
                  : toBillingDetailsInput(value.billing),
              },
            },
          });

          if (!saved?.savePaymentMethod) return;
        } else {
          paymentMethodUuid = value.selected;
        }

        // 3. Create the subscription against the chosen card — a saved card by
        // our uuid (resolved and ownership-checked server-side) or the
        // just-attached card by its Stripe id.
        const { data: intentData } = await createSubscriptionIntent({
          variables: {
            input: {
              subscriptionType: SubscriptionType.essentials,
              priceStep,
              paymentMethodId,
              paymentMethodUuid,
            },
          },
        });

        const result = intentData?.createSubscriptionIntent;

        if (!result) return;

        // 4. Confirm the first invoice's payment (3DS / SCA if required).
        if (!result.succeeded && result.clientSecret) {
          const { error: confirmError } = await stripe.confirmCardPayment(
            result.clientSecret
          );

          if (confirmError) {
            setCardError(confirmError.message ?? 'Card authentication failed.');
            return;
          }
        } else if (!result.succeeded) {
          setCardError('Payment could not be completed.');
          return;
        }

        // The new plan is applied locally by the `invoice.paid` webhook, so
        // an immediate refetch can race it — an in-place upgrade's local row
        // keeps the old subtotal until the webhook lands. Poll the profile
        // briefly until the purchased step (and the limitations derived from
        // it) is visible, so gated screens like domains unlock without a
        // manual refresh. Falls through after ~8s and lets the usual
        // background refetches catch up.
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const { data: refreshed } = await apolloClient.query({
            query: GQL_QUERY_ME,
            fetchPolicy: 'network-only',
          });

          const refreshedUser = refreshed
            ? getFragmentData(GQL_FRAGMENT_USER, refreshed.user)
            : null;

          const refreshedSubscription = refreshedUser?.company.subscription;

          if (
            refreshedSubscription?.type === SubscriptionType.essentials &&
            refreshedSubscription.subtotal === selectedStep?.priceGbp
          )
            break;

          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        await apolloClient.refetchQueries({
          include: [GQL_QUERY_LIST_PAYMENT_METHODS, GQL_QUERY_READ_USAGE],
        });

        hasCompleted = true;

        track('Checkout Completed', {
          subscriptionType: SubscriptionType.essentials,
          isUpgrade: !!user.company.subscription,
        });

        notify.success(`Subscribed to ${plan?.name ?? 'plan'}.`);

        onSuccess?.();

        onClose();
      } finally {
        if (!hasCompleted)
          track('Checkout Failed', {
            subscriptionType: SubscriptionType.essentials,
          });

        setLoading(false);
      }
    },
  });

  const billing = useStore(form.store, (state) => state.values.billing);
  const isAddNew = useStore(
    form.store,
    (state) => state.values.selected === ADD_NEW_PAYMENT_METHOD
  );

  // Preview off the freshly entered country when adding a card, else the
  // company's stored declared country (a saved card was determined from it).
  const previewCountry = billing.country || user.company.billingCountry;

  return (
    <form
      className="flex flex-col grow min-h-0"
      onSubmit={(e) => {
        e.preventDefault();

        void form.handleSubmit();
      }}
    >
      <div className="p-5 space-y-4 grow overflow-y-auto overflow-x-hidden">
        {step === 'pick' && (
          <form.Field
            name="selected"
            validators={{
              onSubmit: ({ value }) =>
                value ? undefined : 'Choose a card to charge.',
            }}
            children={({ state, handleChange }) => (
              <PaymentMethodSelector
                paymentMethods={cards}
                value={state.value}
                onChange={handleChange}
                error={state.meta.errors.join(', ')}
              />
            )}
          />
        )}

        {step === 'address' && (
          <BillingDetailsFields form={form} fields="billing" />
        )}

        {step === 'card' && (
          <NewCardForm
            ref={newCardRef}
            defaultName={user.name ?? ''}
            address={
              hasCompanyAddress
                ? {
                    postcode: user.company.billingPostalCode ?? '',
                    country: user.company.billingCountry ?? undefined,
                  }
                : {
                    postcode: billing.postcode,
                    country: billing.country || undefined,
                  }
            }
          />
        )}

        {cardError && <p className="text-sm text-red-300">{cardError}</p>}

        {plan && (
          <div className="pt-2 space-y-3">
            <h3 className="font-grotesque text-h3 font-semibold text-white">
              Subscription details
            </h3>
            <div className="flex items-center justify-between text-body-medium">
              <span className="text-white">{plan.name}</span>
              <span className="text-amber-400">{formatGbp(plan.price)}</span>
            </div>
            {upgradeDiscount > 0 && (
              <>
                <div className="flex items-center justify-between text-body-medium">
                  <span className="text-white">Unused allowance credit</span>
                  <span className="text-amber-400">
                    −{formatGbp(upgradeDiscount)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-body-medium border-t border-grey-t-700 pt-3">
                  <span className="text-white">Subtotal</span>
                  <span className="text-amber-400">
                    {formatGbp(chargeToday)}
                  </span>
                </div>
              </>
            )}
            <TaxSummary
              subtotal={chargeToday}
              country={previewCountry}
              totalLabel={
                upgradeDiscount > 0
                  ? `(renews at ${formatGbp(plan.price)} + tax every month)`
                  : '(repeats every month)'
              }
            />
          </div>
        )}
      </div>
      <div className="shrink-0">
        <ModalFooter
          submitText={
            step === 'card' || (step === 'pick' && !isAddNew)
              ? 'Subscribe & Pay'
              : 'Next'
          }
          asForm
          onClose={
            step === 'card' && !hasCompanyAddress
              ? () => setStep('address')
              : step !== 'pick' && cards.length > 0
                ? () => setStep('pick')
                : onClose
          }
          closeText={
            (step === 'card' && !hasCompanyAddress) ||
            (step !== 'pick' && cards.length > 0)
              ? 'Back'
              : 'Cancel'
          }
          buttonsDisabled={loading || !stripe}
        />
      </div>
    </form>
  );
};

export const UpgradeCheckoutModal = ({
  open,
  onClose,
  onSuccess,
  priceStep,
}: Props): ReactElement => {
  // Re-mount Elements per opening so the form state resets between attempts.
  const elementsKey = useMemo(() => (open ? Date.now() : 0), [open]);
  const [loading, setLoading] = useState(false);

  const { user } = useUser();

  const isUpgrade = !!user.company.subscription;

  useEffect(() => {
    if (open)
      track('Checkout Opened', {
        subscriptionType: SubscriptionType.essentials,
        isUpgrade,
      });
  }, [open, isUpgrade]);

  return (
    <Modal
      title="Subscribe"
      open={open}
      // Block closing (backdrop, escape, X) while the payment is in flight so
      // the user can't dismiss the modal mid-charge.
      onClose={loading ? undefined : onClose}
      loading={loading}
      dialogPanelClassname="w-150"
      scroll={false}
    >
      <AccountDetailsGate description="First, add your details — they appear on your invoices.">
        {stripePromise ? (
          <Elements
            key={elementsKey}
            stripe={stripePromise}
            options={{ appearance: { theme: 'night' } }}
          >
            <Suspense
              fallback={
                <div className="p-10 flex justify-center">
                  <FullLoader className="size-6" />
                </div>
              }
            >
              <CheckoutForm
                priceStep={priceStep}
                onClose={onClose}
                onSuccess={onSuccess}
                loading={loading}
                setLoading={setLoading}
              />
            </Suspense>
          </Elements>
        ) : (
          <div className="p-5">
            <Alert type="serious" message="Something went wrong." />
          </div>
        )}
      </AccountDetailsGate>
    </Modal>
  );
};
